// Shared rendering helpers for an audit_logs row - originally built for
// Logs.jsx's "Log Details" popup, and reused as-is by Users.jsx's "User
// Activity" popup so both pages present an audit entry the same way
// (colored operation tag, readable sentence-diff) instead of drifting into
// two different-looking implementations of the same thing.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import startCase from "lodash/startCase";

dayjs.extend(utc);
dayjs.extend(timezone);

// One distinct color per real operation type, so delete/reject/approve/
// merge/restore/login/signup are never all indistinguishable red Tags.
export const OPERATION_COLORS = {
  insert: "green",
  update: "blue",
  delete: "red",
  approve: "cyan",
  reject: "orange",
  merge: "purple",
  restore: "gold",
  login: "lime",
  signup: "geekblue",
};

// Matches a MySQL DATETIME ("YYYY-MM-DD HH:mm:ss") or an ISO 8601 timestamp
// (with an optional fractional-seconds/timezone suffix) - deliberately
// narrow so a diff on an unrelated numeric/string field (an id, a status
// code, etc.) is never mistaken for a date and reformatted.
const DATE_LIKE_PATTERN = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export const formatIfDate = (val) => {
  if (typeof val !== "string" || !DATE_LIKE_PATTERN.test(val)) return null;
  const parsed = dayjs(val).tz("Asia/Kolkata");
  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm:ss") : null;
};

// Bookkeeping columns that change on virtually every write but never mean
// anything to a reader - always filtered out of the readable diff below so
// the list stays focused on fields an admin actually changed.
const NOISE_FIELDS = new Set(["updated_at", "modified_at", "created_at", "id"]);

export const formatDiffValue = (val) => {
  if (val === null || val === undefined || val === "") return "empty";
  const asDate = formatIfDate(val);
  if (asDate) return asDate;
  return String(val);
};

// A nested field (e.g. a role's `permissions`) isn't always stored the same
// way on both sides of a diff - sometimes it's a real nested object by the
// time old_data/new_data's outer JSON is parsed, sometimes it's still its
// own JSON *string* one level down (double-encoded). Both need to end up as
// an actual object before rendering, or a still-encoded side prints as raw
// JSON text while the already-decoded side prints "[object Object]".
const parseMaybeJSON = (val) => {
  if (typeof val !== "string") return val;
  const trimmed = val.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return val;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return val;
  }
};

const isPlainObject = (val) => val !== null && typeof val === "object" && !Array.isArray(val);

const cellStyle = { padding: "4px 8px", border: "1px solid #f0f0f0", textAlign: "left" };

// Renders a changed object-valued field (permissions, settings blobs, etc.)
// as a small table of just the sub-keys that actually differ, rather than
// dumping the entire object on one line - a role with 80 permission keys
// only needs the 2 that were toggled shown, not all 80.
const renderObjectDiff = (oldObj, newObj) => {
  const subKeys = Array.from(
    new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
  ).filter((k) => formatDiffValue(oldObj[k]) !== formatDiffValue(newObj[k]));

  if (subKeys.length === 0) return null;

  return (
    <table style={{ borderCollapse: "collapse", marginTop: 6, width: "100%", maxWidth: 480 }}>
      <thead>
        <tr>
          <th style={cellStyle}>Field</th>
          <th style={cellStyle}>Old</th>
          <th style={cellStyle}>New</th>
        </tr>
      </thead>
      <tbody>
        {subKeys.map((k) => (
          <tr key={k}>
            <td style={cellStyle}>{startCase(k)}</td>
            <td style={{ ...cellStyle, color: "#888" }}>{formatDiffValue(oldObj[k])}</td>
            <td style={cellStyle}><strong>{formatDiffValue(newObj[k])}</strong></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Renders an audit_logs row's old_data/new_data as a plain-English list of
// what changed (e.g. "Status changed from Offline to Online"), instead of a
// raw key/value diff table.
export const renderLogDiff = (oldData, newData) => {
  const oldObj = typeof oldData === "string" ? JSON.parse(oldData || "{}") : oldData || {};
  const newObj = typeof newData === "string" ? JSON.parse(newData || "{}") : newData || {};

  const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]));
  const changes = allKeys
    .filter((key) => !NOISE_FIELDS.has(key))
    .map((key) => ({
      key,
      oldVal: parseMaybeJSON(oldObj[key]),
      newVal: parseMaybeJSON(newObj[key]),
    }))
    .filter(({ oldVal, newVal }) => JSON.stringify(oldVal) !== JSON.stringify(newVal));

  if (changes.length === 0) {
    return (
      <div style={{ color: "#888", fontStyle: "italic", padding: "8px" }}>
        No field changes recorded for this entry.
      </div>
    );
  }

  return (
    <ul style={{ margin: 0, padding: "8px 8px 8px 24px" }}>
      {changes.map(({ key, oldVal, newVal }) => {
        if (isPlainObject(oldVal) || isPlainObject(newVal)) {
          const oldSub = isPlainObject(oldVal) ? oldVal : {};
          const newSub = isPlainObject(newVal) ? newVal : {};
          return (
            <li key={key} style={{ marginBottom: 10 }}>
              <strong>{startCase(key)}</strong> changed:
              {renderObjectDiff(oldSub, newSub)}
            </li>
          );
        }
        return (
          <li key={key} style={{ marginBottom: 6 }}>
            <strong>{startCase(key)}</strong> changed from{" "}
            <span style={{ color: "#888" }}>{formatDiffValue(oldVal)}</span> to{" "}
            <strong>{formatDiffValue(newVal)}</strong>
          </li>
        );
      })}
    </ul>
  );
};
